import { Box, useMediaQuery } from '@mui/material'
import { Formik } from 'formik'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import { useTranslation } from 'react-i18next'
import { usePlaces } from 'providers/PlacesProvider'
import { DeliveriesForPlace } from 'types/model/order'
import * as yup from 'yup'
import {
  useMutateNewDeliveryRoute,
  useMutateUpdateDeliveryShipments,
} from 'hooks/useDeliveries'
import { generateCrc, isNotBool } from 'lib/utils'
import { uniq } from 'lodash'
import { useAlert } from 'providers/AlertProvider'
import { TRACKING_EVENT } from 'types/model/delivery'
import theme from 'lib/theme'
import TagManager from 'react-gtm-module'
import { useQueryClient } from 'react-query'
import { OrderListingWrapper } from './elements'
import RouteOrdersView from './RouteOrdersView'
import { ListOfAllOrders } from './ListOfAllOrders'

export type RoutePlanningFormProps = {
  orders: {
    orderId: string
    deliveryId: string
    latlng: string
    routeName: string
  }[]
  startTime: string
  toggle: boolean
  submitType: string
  actionType: string
}

const OrderListing = () => {
  const { t } = useTranslation()
  const {
    ordersToDisplay,
    toggleDeliveriesToDisplay,
    deliveries,
    deliveriesFromApi,
    ordersFromApi,
    orderData: allOrders,
  } = useRoutePlanning()
  const { selectedPlace } = usePlaces()
  const oneShipmentSelected = typeof toggleDeliveriesToDisplay === 'number'

  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialValues = {
    // part Ids stored in orders
    orders: [],
    startTime:
      ordersToDisplay?.[0]?.shipmentDeliveryTimes?.shipmentStartTime || '',
    toggle: false,
    submitType: '',
    actionType: '',
  }

  const { mutateAsync } = useMutateNewDeliveryRoute(
    selectedPlace?.masterId as string
  )
  const { mutateAsync: mutateUpdateDeliveryShipments } =
    useMutateUpdateDeliveryShipments(selectedPlace?.masterId as string)
  const queryClient = useQueryClient()

  const handleOrders = async (values: RoutePlanningFormProps) => {
    const eventVariables = {
      placeId: selectedPlace?.masterId || '',
      placeName: selectedPlace?.name || '',
      organizationId: selectedPlace?.organizationId || '',
      actionType: values.actionType,
    }
    if (values.submitType === 'createShipment') {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.CREATE_NEW_ROUTE,
          ...eventVariables,
        },
      })
      const shipmentOrder = values.orders.map((value, i) => {
        return {
          id: Number(value.orderId),
          seq: i,
        }
      })

      const deliveryIds = values.orders.map(
        (order) => Number(order.deliveryId) || NaN
      )
      const uniqueDeliveryIds = uniq(deliveryIds)

      const crcsWithIds = generateCrc(uniqueDeliveryIds, deliveriesFromApi) as {
        crc: string
        deliveryId: number
      }[]

      const deliveryIdsinOrder = crcsWithIds.map((item) => item.deliveryId)
      const crcsInOrder = crcsWithIds.map((item) => item.crc)

      const payload = {
        shipments: [
          {
            group_index: Math.floor(Math.random() * (530 - 30) + 30),
            orders: shipmentOrder,
          },
        ],
        delivery_ids: deliveryIdsinOrder,
        delivery_crcs: uniq(crcsInOrder),
        message: 'new route',
      }

      await mutateAsync(payload, {
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
      })
    } else if (values.submitType.includes('removeFromRoute')) {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.REMOVE_FROM_ROUTE,
          ...eventVariables,
        },
      })
      const selectedOrderId = values.submitType.split('/')[1].trim()

      const selectedOrderDeliveryId = Number(
        values.submitType.split('/')[2].trim()
      )
      const routeName = values.submitType.split('/')[3].trim()

      const selectedShipment =
        deliveries?.filter((delivery) => delivery.routeName === routeName) || []

      const ordersToBeDeleted =
        selectedShipment[0]?.shipmentOrders
          .map((order) => {
            if (order.orderId === Number(selectedOrderId)) {
              return order.orderId
            }
            return false
          })
          .filter(isNotBool) || []

      const deliveryIdsInShipment = ordersToBeDeleted
        .map((id) => {
          const orderdata = ordersFromApi?.filter(
            (order) => Number(order.parts[0].id) === Number(id)
          )
          if (orderdata) {
            return Number(orderdata[0].parts[0].deliveryId)
          }
          return false
        })
        .filter(isNotBool)

      const uniqueDeliveryIds =
        deliveryIdsInShipment.length > 0
          ? uniq(deliveryIdsInShipment).filter(Boolean)
          : [selectedOrderDeliveryId]

      const crcsWithIds = generateCrc(uniqueDeliveryIds, deliveriesFromApi) as {
        crc: string
        deliveryId: number
      }[]

      const deliveryIdsinOrder = crcsWithIds.map((item) => item.deliveryId)
      const crcsInOrder = crcsWithIds.map((item) => item.crc)

      const ordersInPayload = ordersToBeDeleted.map((id, i) => ({
        id: Number(id),
        seq: i,
      }))

      /**
       * If you have a route with multiple orders
       * and want to completely remove some orders from the route (without placing them into another route)
       * you can achieve this with the group_index = -1.
       * So you pass the order ids that you want to delete from the route with `group_index = -1`
       * and the other orders still stay in a route while the selected ones are deleted
       */

      const payload = {
        shipments: [
          {
            group_index: -1,
            orders: ordersInPayload,
          },
        ],
        delivery_crcs: crcsInOrder,
        delivery_ids: deliveryIdsinOrder,
        message: 'remove order',
      }

      await mutateUpdateDeliveryShipments(payload, {
        onSuccess: async () => {
          queryClient.refetchQueries(['deliveries'])
          queryClient.refetchQueries(['ordersForPlace'])
        },
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
      })
    } else if (values.submitType.includes('removeMultipleFromRoute')) {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.REMOVE_FROM_ROUTE,
          ...eventVariables,
        },
      })
      const orderPartIds = values.orders.map((order) => order.orderId)
      const deliveryIds = values.orders.map((order) => Number(order.deliveryId))
      const ordersInPayload = orderPartIds.map((id, i) => ({
        id: Number(id),
        seq: i,
      }))

      const uniqueDeliveryIds = uniq(deliveryIds).filter(Boolean)

      const crcsWithIds = generateCrc(uniqueDeliveryIds, deliveriesFromApi) as {
        crc: string
        deliveryId: number
      }[]

      const deliveryIdsinOrder = crcsWithIds.map((item) => item.deliveryId)
      const crcsInOrder = crcsWithIds.map((item) => item.crc)

      const payload = {
        shipments: [
          {
            group_index: -1,
            orders: ordersInPayload,
          },
        ],
        delivery_crcs: crcsInOrder,
        delivery_ids: deliveryIdsinOrder,
        message: 'remove order',
      }
      await mutateUpdateDeliveryShipments(payload, {
        onSuccess: async () => {
          queryClient.refetchQueries(['deliveries'])
          queryClient.refetchQueries(['ordersForPlace'])
        },
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
      })
    } else {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.ADD_TO_ROUTE,
          ...eventVariables,
        },
      })
      const ordersInEditingShipment = values.submitType
        .split('/')[2]
        .trim()
        .split(',')

      const deliveryIdsInShipment = ordersInEditingShipment
        .map((id) => {
          const orderdata = ordersFromApi?.filter(
            (order) => Number(order.parts[0].id) === Number(id)
          )
          if (orderdata) {
            return Number(orderdata[0].parts[0].deliveryId)
          }
          return false
        })
        .filter(isNotBool)

      const groupIndex = () => {
        const groupIndexArray = ordersInEditingShipment.map((id) => {
          const orderdata = ordersFromApi?.filter(
            (order) => Number(order.parts[0].id) === Number(id)
          )
          if (orderdata && allOrders) {
            return allOrders.filter(
              (order: DeliveriesForPlace) =>
                order.parts[0].id === orderdata[0].parts[0].id
            )[0].shipmentData[0].groupIndex
          }
          return null
        })
        return groupIndexArray[0] || Math.floor(Math.random() * (530 - 30) + 30)
      }

      const deliveryIdsFromSelectedOrders = values.orders.map((order) =>
        Number(order.deliveryId)
      )
      const uniqueDeliveryIds = uniq([
        ...deliveryIdsInShipment,
        ...deliveryIdsFromSelectedOrders,
      ]).filter(Boolean)

      const crcsWithIds = generateCrc(uniqueDeliveryIds, deliveriesFromApi) as {
        crc: string
        deliveryId: number
      }[]

      const deliveryIdsinOrder = crcsWithIds.map((item) => item.deliveryId)
      const crcsInOrder = crcsWithIds.map((item) => item.crc)
      const ordersExistingInShipment = ordersInEditingShipment.map((id, i) => ({
        id: Number(id),
        seq: i,
      }))

      const ordersToBeAddedToShipment = values.orders.map((value, i) => {
        return {
          id: Number(value.orderId),
          seq: i + ordersInEditingShipment.length,
        }
      })

      const payload = {
        shipments: [
          {
            group_index: groupIndex(),
            orders: ordersExistingInShipment.concat(ordersToBeAddedToShipment),
          },
        ],
        delivery_crcs: crcsInOrder,
        delivery_ids: deliveryIdsinOrder,
        message: 'edit route',
      }
      await mutateUpdateDeliveryShipments(payload, {
        onSuccess: async () => {
          queryClient.refetchQueries(['deliveries'])
          queryClient.refetchQueries(['ordersForPlace'])
        },
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'left',
          })
        },
      })
    }
  }

  const schema = yup.object({
    startTime: yup
      .string()
      .length(5)
      .matches(/(\d){2}:(\d){2}/)
      .nullable(),
  })
  return (
    <OrderListingWrapper aria-label={t('orderListingAsideAria')}>
      <Formik
        enableReinitialize
        onSubmit={handleOrders}
        validationSchema={schema}
        initialValues={initialValues}>
        {ordersToDisplay && ordersToDisplay.length > 0 ? (
          <Box>
            {oneShipmentSelected ? (
              <RouteOrdersView />
            ) : (
              <ListOfAllOrders
                ordersToDisplay={ordersToDisplay}
                deliveries={deliveries}
              />
            )}
          </Box>
        ) : null}
      </Formik>
    </OrderListingWrapper>
  )
}

export default OrderListing
